/**
 * Homepage context story: where founder-held context breaks (use cases) and
 * the cards that show how Cooperly keeps that context (foundations).
 *
 * The people are the fictional Demo Team from the Six Pillars preview
 * (lib/six-pillars-preview.ts). The owner removed the "Illustrative example"
 * caption from these visuals; do not add it back.
 */

export const contextStoryCast = {
  priya: { name: "Priya Dawson", role: "Product", avatarSrc: "/cooperly/people/priya-dawson.png" },
  adrian: { name: "Adrian Cole", role: "Team lead", avatarSrc: "/cooperly/people/adrian-cole.png" },
  james: { name: "James Bremer", role: "Backend", avatarSrc: "/manual/avatars/avatar_02.png" },
  serena: { name: "Serena Holt", role: "Design", avatarSrc: "/cooperly/people/serena-holt.png" },
  julian: { name: "Julian Park", role: "Engineering", avatarSrc: "/manual/avatars/avatar_04.png" },
} as const;

export type ContextStoryPersonId = keyof typeof contextStoryCast;

export type FounderBreakPatternId = "01" | "02" | "03" | "04";

export type FounderBreakPattern = {
  id: FounderBreakPatternId;
  title: string;
  explanation: string;
  /**
   * flow: lines from the team converge into a knot in front of the founder.
   * flood: the questions themselves pile up beside a counter.
   * pingpong: one ticket loops between two functions, then lands on the founder.
   * echo: an agreement dissolves over time while the same question keeps returning.
   */
  visual: "flow" | "flood" | "pingpong" | "echo";
  /**
   * What reaches the founder, newest first. Flow and flood steps cycle through these as new
   * arrivals; on the flow step they arrive faster and faster until the founder gives out.
   */
  messages: readonly { from: ContextStoryPersonId; text: string; when?: string }[];
  /** Flood only: how many questions have piled up, and what they are. */
  pile?: { count: number; caption: string };
  /** Echo only: the agreement the team made before the question started coming back. */
  agreement?: { when: string; text: string };
  meta: string;
};

export const founderBreakPatterns: readonly FounderBreakPattern[] = [
  {
    id: "01",
    title: "Decisions come back to you",
    explanation:
      "The team ships faster with AI, but the reasons behind a decision stay in your head. So it comes back for you to explain or sign off, even after you handed it over.",
    visual: "flow",
    messages: [
      { from: "priya", text: "Can you confirm the launch scope?" },
      { from: "james", text: "Are we still freezing the API in March?" },
      { from: "serena", text: "Which onboarding flow did we pick?" },
      { from: "julian", text: "Can you sign off the pricing change?" },
      { from: "adrian", text: "Why did we drop the referral feature?" },
      { from: "priya", text: "Is the beta still on for Friday?" },
      { from: "james", text: "Who decided we skip the load test?" },
      { from: "serena", text: "Do we keep the old dashboard?" },
    ],
    meta: "Each one waits for your answer",
  },
  {
    id: "02",
    title: "New leads still manage through you",
    explanation:
      "Your new lead can see the backlog. They can’t see that Julian takes feedback better in writing, so they check with you before every hard conversation.",
    visual: "flood",
    messages: [
      { from: "adrian", text: "How should I give Julian this feedback?", when: "Today" },
      { from: "adrian", text: "Can James skip Thursday’s review?", when: "Yesterday" },
      { from: "adrian", text: "Who decides the onboarding scope?", when: "Monday" },
      { from: "adrian", text: "Is Serena okay with the new deadline?", when: "Last week" },
      { from: "adrian", text: "Should Priya or I run the retro?", when: "Last week" },
      { from: "adrian", text: "Can I tell the team the launch moved?", when: "2 weeks ago" },
      { from: "adrian", text: "Who approves Julian’s time off?", when: "2 weeks ago" },
    ],
    pile: { count: 11, caption: "questions from Adrian in his first three weeks as lead" },
    meta: "Team lead for three weeks",
  },
  {
    id: "03",
    title: "The same handoff keeps breaking",
    explanation:
      "Product and Engineering each have a definition of done, and nobody wrote either one down. The ticket bounces between them until you pick one.",
    visual: "pingpong",
    messages: [
      { from: "priya", text: "It’s done when design signs off." },
      { from: "james", text: "It’s done when it ships." },
    ],
    meta: "Reopened five times this quarter",
  },
  {
    id: "04",
    title: "Fixes are agreed, then forgotten",
    explanation:
      "After the last launch, the team agreed to change the QA handoff. Nobody checked whether it stuck, so you find out when the same problem reaches you again.",
    visual: "echo",
    agreement: { when: "March", text: "Felix signs off QA before every release." },
    // Oldest first: each one returns at its point on the timeline.
    messages: [
      { from: "julian", text: "Who owns QA after the March change?", when: "6 weeks ago" },
      { from: "priya", text: "Are we still doing the QA check we agreed on?", when: "3 weeks ago" },
      { from: "james", text: "Who signs off QA this time?", when: "Last week" },
      { from: "serena", text: "Didn’t we change this after the last launch?", when: "Today" },
    ],
    meta: "Agreed in March, never checked",
  },
];

/**
 * The light scene above each foundation card. Each one answers a founder break
 * above with the same mark for "You", now an empty ring off to the side:
 * record (01): the team's routes run into a dated fact instead of jamming at you;
 * handover (02): a new Co-Lead reads the whole team instead of asking you;
 * holding (04): Pulse shows an agreed fix holding, so nothing comes back to you.
 */
export type FoundationScene = "record" | "handover" | "holding";

export type FoundationCard = {
  id: "memory" | "co-leads" | "before-after";
  title: string;
  description: string;
  scene: FoundationScene;
};

/**
 * "Stop being the bottleneck" cards: one value per card. Claims follow the
 * product: dated facts on a team or a person, Co-Lead read access with the
 * weekly lead summary, and weekly Pulse that keeps running after a change.
 */
export const foundationCards: readonly FoundationCard[] = [
  {
    id: "memory",
    title: "You stop being the only one who knows why",
    description:
      "Save each decision or agreement with its date and reason, on the team or the person. The next lead finds it there instead of asking you.",
    scene: "record",
  },
  {
    id: "co-leads",
    title: "New managers stop running every call past you",
    description:
      "Add them as Co-Lead. From day one they read the Team Profile, Fundamentals and Pulse results, plus a weekly summary: the context they would otherwise ask you for.",
    scene: "handover",
  },
  {
    id: "before-after",
    title: "Know if a fix worked before it comes back to you",
    description:
      "Weekly Pulse keeps running after the team agrees on a change, so you see whether things got better or slid back without waiting for the next escalation.",
    scene: "holding",
  },
];

export type FoundationExtra = {
  id: "digest" | "next-step" | "goal" | "one-on-one" | "check-ins" | "anonymous" | "reminders" | "manual";
  title: string;
  description: string;
};

/** The smaller routines under the cards that keep the team running without the founder. */
export const foundationExtras: readonly FoundationExtra[] = [
  {
    id: "digest",
    title: "Leads get a weekly summary",
    description: "One digest of every team they run, by email, Slack or Telegram.",
  },
  {
    id: "next-step",
    title: "One clear next step a week",
    description: "Cooperly picks it from the team roadmap and sends it to each lead.",
  },
  {
    id: "goal",
    title: "Everyone knows what matters now",
    description: "Write the team’s goal once. Team Overview and connected AI tools read it from there.",
  },
  {
    id: "one-on-one",
    title: "Ready questions for every 1:1",
    description: "Your connected Claude or ChatGPT drafts them from how this person works.",
  },
  {
    id: "check-ins",
    title: "Check-ins without another app",
    description: "People answer Pulse, Fundamentals and Coop Profile in a Slack DM. Nothing is posted to a channel.",
  },
  {
    id: "anonymous",
    title: "Safe to answer honestly",
    description: "Pulse and Fundamentals answers are anonymous, so people can say what they really think.",
  },
  {
    id: "reminders",
    title: "No chasing people to reply",
    description: "Cooperly invites everyone when a check-in opens and reminds them about Fundamentals each week.",
  },
  {
    id: "manual",
    title: "Everyone explains how they like to work",
    description: "Each person writes a short manual on how they work and shares it with their lead.",
  },
];
